'use client';

import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import Image from 'next/image';
import { AlertTriangle, Check, ChevronDown, Copy, ExternalLink, Gauge, Grid3X3, LifeBuoy, Loader2, LogOut, Sparkles, User2, Zap } from 'lucide-react';
import Link from 'next/link';
import { formatEther, formatUnits } from 'viem';
import { useBalance } from 'wagmi';
import { cn, truncateAddress } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { GRAPHICS_LEVELS, GRAPHICS_PRESETS, type GraphicsLevel } from '../settings/graphics-settings';
import { useGraphics } from '../settings/GraphicsContext';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useProfileModal } from '@/contexts/profile-modal-context';
import { useWallet } from '@/hooks/use-wallet';
import { useUserProfile } from '@/hooks/use-user-profile';
import { useEthPrice } from '@/hooks/use-eth-price';
import { useTakeoverPrice } from '@/hooks/use-takeover-price';
import { prefetchProfileModalBundle } from '@/lib/prefetch-profile-modal';

const LEVEL_ICONS: Record<GraphicsLevel, JSX.Element> = {
  lite: <Grid3X3 className="h-3 w-3" />,
  medium: <Gauge className="h-3 w-3" />,
  high: <Sparkles className="h-3 w-3" />,
  ultra: <Zap className="h-3 w-3" />,
};

type BalanceSymbol = 'TAKEOVER' | 'ETH' | 'USDC';

// Mirrors the resolution in `next.config.js` so the support link in the
// dropdown footer always points at the same classic-app origin the app
// already redirects `/boardroom/classic` to. `replace(/\/$/, '')`
// trims a trailing slash so concatenation later (if needed) stays clean.
const CLASSIC_APP_URL = (
  process.env.NEXT_PUBLIC_CLASSIC_APP_URL || 'https://classic.takeover.fun'
).replace(/\/$/, '');

function fmtTokenAmount(n: number, maxFrac = 4): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(2)}K`;
  if (n >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toLocaleString(undefined, { maximumFractionDigits: maxFrac });
}

const USD_FMT_FINE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 4,
});
const USD_FMT_COARSE = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function fmtUsd(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return (n < 1 ? USD_FMT_FINE : USD_FMT_COARSE).format(n);
}

export function PortfolioDropdown() {
  const queryClient = useQueryClient();
  const { openProfile } = useProfileModal();
  const { level, auto, setLevel, setAuto } = useGraphics();
  const wallet = useWallet();
  const {
    avatarUrl: profileAvatar,
    displayName: profileDisplayName,
    level: profileLevel,
    // `isProfileLoading` (ungated) is true when the cached profile
    // doesn't belong to the current address — either first fetch or the
    // stale-placeholder window right after a wallet switch. Drives the
    // Loader2 avatar fallback + the displayName skeleton below.
    isProfileLoading,
  } = useUserProfile(wallet.address);
  const { ethPrice } = useEthPrice();
  const takeoverPrice = useTakeoverPrice();
  const [copied, setCopied] = useState(false);

  const { data: nativeBal, isLoading: nativeLoading } = useBalance({
    address: (wallet.address ?? undefined) as `0x${string}` | undefined,
    chainId: wallet.expectedChainId ?? undefined,
    query: { enabled: !!wallet.address },
  });

  // After a wallet switch, `use-wallet` calls `queryClient.resetQueries` for
  // `usdc-balance-cached` / `takeover-balance`, so both observers flip to
  // `isLoading: true` with `data: undefined`. Combine with wagmi's native
  // balance loading flag so a single fetch in-flight keeps all three rows on
  // skeletons — better than showing `0` (looks empty) or the previous
  // wallet's numbers (looks stale).
  const isBalancesLoading =
    !!wallet.address && (wallet.isBalancesLoading || nativeLoading);
  const isAvatarLoading = isProfileLoading || wallet.isLinkingAccount;

  const balanceRows = useMemo(() => {
    if (!wallet.address) return [];
    const toNum = (x: bigint, decimals: number) => Number(formatUnits(x, decimals));
    const takeoverAmt = toNum(wallet.takeoverBalance, 18);
    const usdcAmt = toNum(wallet.usdcBalance, 6);
    const ethAmt = nativeBal?.value ? Number(formatEther(nativeBal.value)) : 0;
    const takeoverUsd =
      takeoverPrice.priceUsd > 0 ? takeoverAmt * takeoverPrice.priceUsd : 0;
    const ethUsd = ethPrice > 0 ? ethAmt * ethPrice : 0;
    const usdcUsd = usdcAmt;

    return [
      {
        symbol: 'TAKEOVER' as const,
        label: 'TAKEOVER',
        balance: fmtTokenAmount(takeoverAmt),
        usd: fmtUsd(takeoverUsd),
      },
      {
        symbol: 'ETH' as const,
        label: 'ETH · Base',
        balance: nativeLoading && !nativeBal ? '…' : fmtTokenAmount(ethAmt),
        usd: fmtUsd(ethUsd),
      },
      {
        symbol: 'USDC' as const,
        label: 'USDC · Base',
        balance: fmtTokenAmount(usdcAmt),
        usd: fmtUsd(usdcUsd),
      },
    ];
  }, [
    wallet.address,
    wallet.takeoverBalance,
    wallet.usdcBalance,
    nativeBal?.value,
    nativeLoading,
    ethPrice,
    takeoverPrice.priceUsd,
  ]);

  const connected = wallet.isConnected && wallet.address;

  const copyAddress = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(wallet.address!);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard denied — silent, dropdown still works
    }
  };

  if (!connected) {
    return (
      <button
        type="button"
        onClick={() => wallet.connectWallet()}
        disabled={!wallet.ready || wallet.connectBusy}
        aria-label="Connect wallet"
        className={cn(
          'flex items-center gap-1.5 h-7 px-2.5 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em] transition-colors',
          'bg-gradient-to-b from-amber-500/15 to-amber-500/5 border-amber-400/40 text-amber-100 hover:border-amber-300/70',
          !wallet.ready && 'opacity-60 pointer-events-none',
        )}
      >
        Connect wallet
      </button>
    );
  }

  const walletAddress = wallet.address!;
  // Drop the old `wallet.displayName` fallback: it comes from Privy's
  // session identity (linked Twitter/Farcaster/email) which is
  // attached to the Privy *user*, not the currently active wallet. On
  // wallet switch it keeps returning the Privy owner's Twitter
  // handle even when the new address has no social link of its own —
  // that's the "avatar/username doesn't change" bug. We now prefer the
  // address-scoped `profileDisplayName` (DB-backed, via
  // `/api/users/[address]`) and fall through to a truncated address.
  const displayName = profileDisplayName || truncateAddress(walletAddress, 4);
  // Same rationale for the avatar — rely on `profileAvatar`
  // (`useUserProfile` already emits a generative avatar derived from
  // the current address when the API hasn't landed yet, and drops the
  // stale placeholder as soon as the address flips).
  const avatarForUi = profileAvatar;
  const levelForUi = profileLevel ?? 0;

  // Wrong-network surfacing — mirrors the classic `WalletDropdown`
  // pattern. `hasCheckedNetwork` is false until wagmi has resolved a
  // chainId for the active connector, so we gate the amber chrome on
  // it to avoid flashing the warning during connect. Without this
  // prompt, users whose wallet enforces a per-dapp-selected-network
  // (MetaMask, Coinbase Wallet, Rabby) get the "Chain ID must match
  // the dApp selected network" wall at swap/submit time with no clue
  // they need to switch; the snackbar `switchChain` preflight in
  // `use-transactions.tsx` recovers them, but one proactive click
  // here saves the extra wallet prompt round-trip.
  const wrongNetwork = wallet.hasCheckedNetwork && !wallet.isCorrectNetwork;

  return (
    <DropdownMenu.Root
      onOpenChange={(open) => {
        if (open && wallet.address) {
          void prefetchProfileModalBundle(queryClient, wallet.address);
        }
      }}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={wrongNetwork ? `Portfolio — wrong network, switch to ${wallet.expectedNetworkName}` : 'Portfolio'}
          title={wrongNetwork ? `Wrong network — switch to ${wallet.expectedNetworkName}` : `${displayName} · portfolio`}
          className={cn(
            'flex items-center gap-1.5 h-7 pl-0.5 pr-2 rounded-[4px] border text-[10px] font-mono uppercase tracking-[0.15em] transition-colors',
            wrongNetwork
              ? 'bg-yellow-500/10 border-yellow-500/50 text-yellow-100 hover:bg-yellow-500/15'
              : 'bg-black/30 hover:bg-white/5 border-amber-500/25 text-zinc-200 hover:text-amber-100',
          )}
        >
          <span className="relative -my-0.5 flex items-center justify-center">
            {isAvatarLoading ? (
              <Avatar className="w-6 h-6 rounded-[4px]">
                <AvatarFallback className="rounded-[4px] bg-amber-500/10 text-amber-200/70">
                  <Loader2 className="h-3 w-3 animate-spin" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <AvatarWithFlair
                avatarUrl={avatarForUi}
                address={walletAddress}
                level={levelForUi}
                size="xs"
                priority
              />
            )}
            {wrongNetwork && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 flex items-center justify-center w-3 h-3 rounded-full bg-yellow-500 ring-1 ring-[#0b0a08] shadow-[0_0_4px_rgba(255,200,60,0.6)]"
              >
                <AlertTriangle className="h-2 w-2 text-black" strokeWidth={3} />
              </span>
            )}
          </span>
          <span className="hidden sm:inline">
            {wrongNetwork ? 'Wrong network' : 'Portfolio'}
          </span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-[60] w-[320px] rounded-md border border-amber-500/30 bg-[#0b0a08]/95 backdrop-blur-md shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-amber-500/15">
            {isAvatarLoading ? (
              <Avatar className="w-10 h-10 rounded-md">
                <AvatarFallback className="rounded-md bg-amber-500/10 text-amber-200/70">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </AvatarFallback>
              </Avatar>
            ) : (
              <AvatarWithFlair
                avatarUrl={avatarForUi}
                address={walletAddress}
                level={levelForUi}
                size="sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {isProfileLoading ? (
                  <span
                    aria-hidden
                    className="inline-block h-3 w-24 rounded-sm bg-zinc-700/40 animate-pulse"
                  />
                ) : (
                  <span className="text-[12px] font-semibold text-zinc-100 truncate leading-tight">
                    {displayName}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={copyAddress}
                className="mt-0.5 group flex items-center gap-1 text-[10px] font-mono tracking-[0.1em] text-zinc-400 hover:text-amber-100 transition-colors"
                title={copied ? 'Copied!' : 'Copy address'}
              >
                <span className="tabular-nums">{truncateAddress(walletAddress)}</span>
                {copied ? (
                  <Check className="h-3 w-3 text-emerald-300" />
                ) : (
                  <Copy className="h-3 w-3 opacity-70 group-hover:opacity-100" />
                )}
              </button>
            </div>
          </div>

          {/* Wrong Network banner + CTA — matches the classic
              `WalletDropdown` behaviour so lobby users get the same
              one-click remediation when their wallet is pinned to the
              wrong chain. Tapping dispatches `wallet_switchEthereumChain`
              via `useSwitchChain`; Radix closes the menu on select,
              which is fine because the dropdown will re-render with
              the correct-network chrome once wagmi emits `chainChanged`. */}
          {wrongNetwork && (
            <div className="p-1 border-b border-amber-500/10">
              <DropdownMenu.Item
                onSelect={() => {
                  void wallet.switchNetwork().catch((err) => {
                    console.error('[PortfolioDropdown] switchNetwork failed', err);
                  });
                }}
                className="flex items-center gap-2 px-2 py-1.5 rounded-[3px] text-[11px] text-yellow-400 hover:text-yellow-200 hover:bg-yellow-500/10 outline-none cursor-default"
              >
                <AlertTriangle className="h-3 w-3 shrink-0" />
                Wrong Network — Switch to {wallet.expectedNetworkName}
              </DropdownMenu.Item>
            </div>
          )}

          {/* View profile sits directly under the avatar/address. */}
          <div className="p-1 border-b border-amber-500/10">
            <MenuLink
              label="View profile"
              icon={<User2 className="h-3 w-3" />}
              onSelect={() => {
                if (!wallet.address) return;
                void prefetchProfileModalBundle(queryClient, wallet.address);
                openProfile(wallet.address);
              }}
            />
            <DropdownMenu.Item
              onSelect={() => wallet.logout()}
              className="flex items-center gap-2 px-2 py-1.5 rounded-[3px] text-[11px] text-loss-red hover:text-loss-red hover:bg-loss-red/10 outline-none cursor-default"
            >
              <LogOut className="h-3 w-3 shrink-0" />
              Disconnect
            </DropdownMenu.Item>
          </div>

          <div className="p-2 flex flex-col gap-1">
            <div className="text-[8px] tracking-[0.28em] font-mono uppercase text-zinc-500 px-1 pb-0.5">Balances</div>
            {balanceRows.map((b) => (
              <div
                key={b.symbol}
                className="flex items-center gap-2 px-2 py-1.5 rounded-[3px] border border-white/[0.04] bg-white/[0.02]"
              >
                <BalanceChip symbol={b.symbol} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-zinc-100 font-medium truncate">{b.label}</div>
                  {isBalancesLoading ? (
                    <span
                      aria-hidden
                      className="mt-0.5 inline-block h-2 w-12 rounded-sm bg-zinc-700/40 animate-pulse"
                    />
                  ) : (
                    <div className="text-[9px] text-zinc-500 font-mono">{b.usd}</div>
                  )}
                </div>
                {isBalancesLoading ? (
                  <span
                    aria-hidden
                    className="inline-block h-2.5 w-16 rounded-sm bg-amber-400/15 animate-pulse"
                  />
                ) : (
                  <div className="text-[11px] font-mono tabular-nums text-amber-100">{b.balance}</div>
                )}
              </div>
            ))}
          </div>

          <div className="p-2 border-t border-amber-500/15">
            <div className="flex items-center justify-between px-1 pb-1">
              <span className="text-[8px] tracking-[0.28em] font-mono uppercase text-zinc-500">Graphics</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setAuto();
                }}
                className={cn(
                  'text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-[3px] border transition-colors',
                  auto
                    ? 'bg-emerald-500/15 border-emerald-400/40 text-emerald-200'
                    : 'bg-transparent border-amber-500/25 text-zinc-300 hover:text-amber-100 hover:border-amber-400/40',
                )}
                title="Use auto-detected quality"
              >
                Auto
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {GRAPHICS_LEVELS.map((l) => {
                const preset = GRAPHICS_PRESETS[l];
                const active = l === level;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setLevel(l);
                    }}
                    className={cn(
                      'flex flex-col items-center gap-1 py-1.5 rounded-[3px] border transition-colors',
                      active
                        ? 'bg-amber-500/10 border-amber-400/50 text-amber-100'
                        : 'bg-black/30 border-white/5 text-zinc-300 hover:text-amber-100 hover:border-amber-400/30',
                    )}
                  >
                    {LEVEL_ICONS[l]}
                    <span className="text-[9px] tracking-[0.15em] uppercase font-mono">{preset.label}</span>
                    {active && <Check className="h-3 w-3 -mt-0.5" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Support link — pinned to the bottom of the dropdown so users
              who hit a bug on the new lobby always have a one-click escape
              hatch back to the classic surface. Renders as a Radix
              `Item asChild` so keyboard/Roving-tabindex focus still works,
              with the `<a target="_blank">` handling the new-tab behaviour
              `MenuLink` (which uses `next/link`) doesn't expose. */}
          <div className="p-1 border-t border-amber-500/15">
            <DropdownMenu.Item asChild>
              <a
                href={CLASSIC_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-2 py-1.5 rounded-[3px] text-[11px] text-zinc-300 hover:text-amber-100 hover:bg-white/5 outline-none cursor-pointer"
              >
                <span className="text-amber-200/80"><LifeBuoy className="h-3 w-3" /></span>
                <span>Any issues? Visit classic.takeover.fun</span>
                <ExternalLink className="ml-auto h-3 w-3 opacity-70" />
              </a>
            </DropdownMenu.Item>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function MenuLink({
  label,
  hint,
  icon,
  onSelect,
  href,
}: {
  label: string;
  hint?: string;
  icon: React.ReactNode;
  /**
   * Fires when the item is activated. The dropdown is allowed to close
   * (we don't `preventDefault`) when a handler is provided so any modal
   * the handler opens animates in against a tidy backdrop. Items with
   * neither `href` nor `onSelect` stay inert (Radix prevents close).
   */
  onSelect?: () => void;
  /** If provided, the item renders as a Next.js <Link>. The dropdown's
   *  root <Item> still handles focus + keyboard activation; Radix's
   *  `asChild` hands off rendering to the Link so client-side routing
   *  works (hard <a> navigation would blow away the in-memory lobby
   *  state). */
  href?: string;
}) {
  const className = "flex items-center gap-2 px-2 py-1.5 rounded-[3px] text-[11px] text-zinc-200 hover:text-amber-100 hover:bg-white/5 outline-none cursor-default data-[disabled]:opacity-60";
  const inner = (
    <>
      <span className="text-amber-200/80">{icon}</span>
      <span>{label}</span>
      {hint && <span className="ml-auto text-[9px] font-mono tracking-[0.18em] uppercase text-zinc-500">{hint}</span>}
    </>
  );

  if (href) {
    return (
      <DropdownMenu.Item asChild>
        <Link href={href} className={className}>
          {inner}
        </Link>
      </DropdownMenu.Item>
    );
  }

  return (
    <DropdownMenu.Item
      onSelect={(e) => {
        if (!onSelect) {
          e.preventDefault();
          return;
        }
        onSelect();
      }}
      className={className}
    >
      {inner}
    </DropdownMenu.Item>
  );
}

/**
 * Balance chip for the portfolio dropdown.
 *
 * - `TAKEOVER` renders the pixel-art `mascot.png` in a bordered rounded
 *   square (same treatment as the header wordmark) so the TKO row reads
 *   as "our token".
 * - `USDC` and `ETH` render inline brand SVGs so the dropdown does not
 *   depend on the `/api/token-icon` proxy for the lobby demo.
 */
function BalanceChip({ symbol }: { symbol: BalanceSymbol }) {
  if (symbol === 'TAKEOVER') {
    return (
      <span
        className="relative w-6 h-6 shrink-0 rounded-[3px] overflow-hidden border border-amber-500/40 shadow-[0_0_4px_rgba(255,215,120,0.35)]"
        aria-hidden
      >
        <Image
          src="/mascot.png"
          alt=""
          fill
          sizes="24px"
          className="object-cover"
          style={{ imageRendering: 'pixelated' }}
        />
      </span>
    );
  }

  if (symbol === 'USDC') {
    return (
      <span
        aria-hidden
        className="w-6 h-6 shrink-0 rounded-full grid place-items-center border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"
        style={{ background: '#2775CA' }}
      >
        <svg viewBox="0 0 32 32" className="w-4 h-4">
          <circle cx="16" cy="16" r="15" fill="none" stroke="#fff" strokeOpacity="0.25" />
          <path
            fill="#fff"
            d="M16 8c-.5 0-.9.4-.9.9v.7c-2.2.3-3.6 1.6-3.6 3.3 0 1.8 1.1 2.7 3.3 3.2l1.2.3c1.4.3 1.9.8 1.9 1.5 0 .9-.9 1.5-2.3 1.5-1.1 0-2-.4-2.4-1.1-.2-.3-.5-.5-.9-.5-.6 0-1 .5-.9 1 .4 1.4 1.6 2.2 3.3 2.4v.7c0 .5.4.9.9.9s.9-.4.9-.9v-.7c2.3-.2 3.8-1.5 3.8-3.4 0-1.8-1.1-2.7-3.3-3.2l-1.2-.3c-1.3-.3-1.9-.8-1.9-1.5 0-.9.8-1.5 2.1-1.5 1 0 1.8.3 2.2 1 .2.3.5.5.9.5.6 0 1-.5.9-1-.4-1.3-1.5-2.1-3.1-2.3v-.7c0-.5-.4-.9-.9-.9z"
          />
        </svg>
      </span>
    );
  }

  // ETH — classic dual-tone diamond on a gunmetal chip.
  return (
    <span
      aria-hidden
      className="w-6 h-6 shrink-0 rounded-full grid place-items-center border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]"
      style={{ background: 'linear-gradient(160deg,#343840,#141619)' }}
    >
      <svg viewBox="0 0 32 32" className="w-3.5 h-4">
        <path fill="#fff" fillOpacity="0.6" d="M16 4v8.6l7 3.1z" />
        <path fill="#fff" d="M16 4L9 15.7l7-3.1z" />
        <path fill="#fff" fillOpacity="0.6" d="M16 21.5v6.5l7-9.7z" />
        <path fill="#fff" d="M16 28v-6.5L9 18.3z" />
        <path fill="#fff" fillOpacity="0.35" d="M16 20.2l7-4.5-7-3.1z" />
        <path fill="#fff" fillOpacity="0.75" d="M9 15.7l7 4.5v-7.6z" />
      </svg>
    </span>
  );
}
